import React, { useEffect, useState, useCallback } from "react";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import {
  PaperPlaneTiltIcon,
  ChatCircleDotsIcon,
  PulseIcon,
  ListChecksIcon,
  ArrowRightIcon,
  ChatCircleTextIcon,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div
    data-testid={testid}
    className="group flex flex-col rounded-sm border border-zinc-200 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
  >
    <div className="flex items-center justify-between">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
        {label}
      </p>
      <Icon size={20} weight="duotone" className={accent || "text-zinc-400"} />
    </div>
    <p className="mt-6 font-display text-5xl font-black tracking-tighter text-zinc-950 tabular-nums">
      {value}
    </p>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState({
    total_replies: 0,
    total_dms: 0,
    today_processed: 0,
    active_keywords: 0,
  });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([api.getStats(), api.listActivity(8)]);
      setStats(s);
      setRecent(a);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Layout
      title="Dashboard"
      subtitle="Real-time overview of your Comment-to-DM automation engine."
    >
      {/* Stat grid */}
      <section className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={PaperPlaneTiltIcon}
          label="Public Replies"
          value={loading ? "—" : stats.total_replies}
          accent="text-amber-500"
          testid="stat-replies"
        />
        <StatCard
          icon={ChatCircleDotsIcon}
          label="Direct Messages"
          value={loading ? "—" : stats.total_dms}
          accent="text-emerald-500"
          testid="stat-dms"
        />
        <StatCard
          icon={PulseIcon}
          label="Today Processed"
          value={loading ? "—" : stats.today_processed}
          accent="text-blue-500"
          testid="stat-today"
        />
        <StatCard
          icon={ListChecksIcon}
          label="Active Keywords"
          value={loading ? "—" : stats.active_keywords}
          accent="text-zinc-700"
          testid="stat-keywords"
        />
      </section>

      {/* Recent activity */}
      <section className="mt-12">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
              Live Feed
            </p>
            <h2 className="font-display text-2xl font-bold tracking-tight text-zinc-950">
              Recent Activity
            </h2>
          </div>
          <Link
            to="/activity"
            className="inline-flex items-center gap-1 text-sm font-semibold text-zinc-700 hover:text-amber-600"
            data-testid="view-all-activity"
          >
            View all <ArrowRightIcon size={14} weight="bold" />
          </Link>
        </div>

        {recent.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-sm border border-dashed border-zinc-300 bg-white py-20">
            <ChatCircleTextIcon size={56} weight="duotone" className="text-zinc-300" />
            <h3 className="mt-4 font-display text-xl font-bold text-zinc-900">
              No activity yet
            </h3>
            <p className="mt-1 max-w-sm text-center text-sm text-zinc-500">
              Once your webhook starts receiving Instagram comments, they will appear
              here in real-time.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-sm border border-zinc-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50">
                <tr className="text-left">
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                    Time
                  </th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                    Comment
                  </th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                    Keyword
                  </th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                    Reply
                  </th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                    DM
                  </th>
                </tr>
              </thead>
              <tbody>
                {recent.map((row) => (
                  <ActivityRow key={row.id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Layout>
  );
}

const StatusBadge = ({ status }) => {
  const cfg = {
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    failed: "bg-red-50 text-red-700 border-red-200",
    skipped: "bg-zinc-50 text-zinc-500 border-zinc-200",
  }[status] || "bg-zinc-50 text-zinc-500 border-zinc-200";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cfg}`}
    >
      {status}
    </span>
  );
};

const ActivityRow = ({ row }) => {
  const t = new Date(row.timestamp);
  const time = t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return (
    <tr className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
      <td className="p-4 font-mono text-xs text-zinc-500">{time}</td>
      <td className="max-w-xs truncate p-4 text-zinc-900">
        <span className="font-mono text-xs text-zinc-500">@{row.commenter_username || "user"}</span>
        <span className="ml-2">{row.comment_text}</span>
      </td>
      <td className="p-4">
        {row.matched_keyword ? (
          <span className="rounded-sm bg-amber-100 px-2 py-1 font-mono text-xs font-bold text-amber-900">
            {row.matched_keyword}
          </span>
        ) : (
          <span className="text-xs text-zinc-400">—</span>
        )}
      </td>
      <td className="p-4">
        <StatusBadge status={row.public_reply_status} />
      </td>
      <td className="p-4">
        <StatusBadge status={row.dm_status} />
      </td>
    </tr>
  );
};
